
      import { createComponent } from '@lit/react'
      import { type EventName } from '@lit/react'
      import * as React from 'react'

      import Component from '../../components/callout/callout.component.js'
      import type { DdsCloseEvent } from '../../events/events'
      export type { DdsCloseEvent } from '../../events/events'

      const tagName = 'dap-ds-callout'

      /**
 * `dap-ds-callout` is a custom callout component.
 * @element dap-ds-callout
 * @title - Callout
 *
 * @event {{ void }} dds-close - Fired when the close button is clicked.
 *
 * @slot - The content of the callout.
 * @slot title - The title of the callout.
 * @slot icon - The icon of the callout.
 * @slot actions - The actions of the callout.
 * @slot close - The close button of the callout.
 *
 * @csspart base - The main callout container.
 * @csspart icon - The icon of the callout.
 * @csspart title - The title of the callout.
 * @csspart description - The description of the callout.
 * @csspart actions - The actions of the callout.
 * @csspart close - The close button of the callout.
 *
 * @cssproperty --dds-callout-padding - Padding of the callout content. Default: var(--dds-spacing-300)
 * @cssproperty --dds-callout-gap - Gap between elements in horizontal layout. Default: var(--dds-spacing-300)
 * @cssproperty --dds-callout-icon-size - Size of the icon. Default: var(--dds-spacing-600)
 * @cssproperty --dds-callout-title-color - Color of the title text. Default: var(--dds-text-neutral-strong)
 * @cssproperty --dds-callout-description-color - Color of the description text. Default: var(--dds-text-neutral-base)
 * @cssproperty --dds-callout-title-font-size - Font size of the title. Default: var(--dds-font-base)
 * @cssproperty --dds-callout-description-font-size - Font size of the description. Default: var(--dds-font-base)
 * @cssproperty --dds-callout-title-font-weight - Font weight of the title. Default: var(--dds-font-weight-bold)
 * @cssproperty --dds-callout-description-font-weight - Font weight of the description. Default: var(--dds-font-weight-medium)
 * @cssproperty --dds-callout-actions-font-weight - Font weight of the actions. Default: var(--dds-font-weight-bold)
 * @cssproperty --dds-callout-actions-gap - Gap between action items. Default: var(--dds-spacing-400)
 * @cssproperty --dds-callout-content-gap - Gap between content elements. Default: var(--dds-spacing-100)
 * @cssproperty --dds-callout-border-radius - Border radius of the callout. Default: var(--dds-radius-base)
 *
 * @cssproperty --dds-callout-brand-background - Brand variant background color. Default: var(--dds-background-brand-base)
 * @cssproperty --dds-callout-brand-background-subtle - Brand variant subtle background color. Default: var(--dds-background-brand-subtle)
 * @cssproperty --dds-callout-brand-background-medium - Brand variant medium background color. Default: var(--dds-background-brand-medium)
 * @cssproperty --dds-callout-brand-background-strong - Brand variant strong background color. Default: var(--dds-background-brand-strong)
 * @cssproperty --dds-callout-brand-icon - Brand variant icon color. Default: var(--dds-icon-brand-subtle)
 * @cssproperty --dds-callout-brand-border - Brand variant border color. Default: var(--dds-border-brand-subtle)
 *
 * @cssproperty --dds-callout-info-background - Info variant background color. Default: var(--dds-background-informative-base)
 * @cssproperty --dds-callout-info-background-subtle - Info variant subtle background color. Default: var(--dds-background-informative-subtle)
 * @cssproperty --dds-callout-info-background-medium - Info variant medium background color. Default: var(--dds-background-informative-medium)
 * @cssproperty --dds-callout-info-background-strong - Info variant strong background color. Default: var(--dds-background-informative-strong)
 * @cssproperty --dds-callout-info-icon - Info variant icon color. Default: var(--dds-icon-informative-subtle)
 * @cssproperty --dds-callout-info-border - Info variant border color. Default: var(--dds-border-informative-subtle)
 *
 * @cssproperty --dds-callout-positive-background - Positive variant background color. Default: var(--dds-background-positive-base)
 * @cssproperty --dds-callout-positive-background-subtle - Positive variant subtle background color. Default: var(--dds-background-positive-subtle)
 * @cssproperty --dds-callout-positive-background-medium - Positive variant medium background color. Default: var(--dds-background-positive-medium)
 * @cssproperty --dds-callout-positive-background-strong - Positive variant strong background color. Default: var(--dds-background-positive-strong)
 * @cssproperty --dds-callout-positive-icon - Positive variant icon color. Default: var(--dds-icon-positive-subtle)
 * @cssproperty --dds-callout-positive-border - Positive variant border color. Default: var(--dds-border-positive-subtle)
 *
 * @cssproperty --dds-callout-warning-background - Warning variant background color. Default: var(--dds-background-warning-base)
 * @cssproperty --dds-callout-warning-background-subtle - Warning variant subtle background color. Default: var(--dds-background-warning-subtle)
 * @cssproperty --dds-callout-warning-background-medium - Warning variant medium background color. Default: var(--dds-background-warning-medium)
 * @cssproperty --dds-callout-warning-background-strong - Warning variant strong background color. Default: var(--dds-background-warning-strong)
 * @cssproperty --dds-callout-warning-icon - Warning variant icon color. Default: var(--dds-icon-neutral-strong)
 * @cssproperty --dds-callout-warning-border - Warning variant border color. Default: var(--dds-border-warning-subtle)
 *
 * @cssproperty --dds-callout-negative-background - Negative variant background color. Default: var(--dds-background-negative-base)
 * @cssproperty --dds-callout-negative-background-subtle - Negative variant subtle background color. Default: var(--dds-background-negative-subtle)
 * @cssproperty --dds-callout-negative-background-medium - Negative variant medium background color. Default: var(--dds-background-negative-medium)
 * @cssproperty --dds-callout-negative-background-strong - Negative variant strong background color. Default: var(--dds-background-negative-strong)
 * @cssproperty --dds-callout-negative-icon - Negative variant icon color. Default: var(--dds-icon-negative-subtle)
 * @cssproperty --dds-callout-negative-border - Negative variant border color. Default: var(--dds-border-negative-subtle)
 */
      const reactWrapper = createComponent({
        tagName,
        elementClass: Component,
        react: React,
        events: {
          onDdsClose: 'dds-close' as EventName<DdsCloseEvent>
        },
        displayName: "DapDSCallout"
      })

      export default reactWrapper
    